import React, { useState, useEffect } from 'react';
import SuperAdminLayout from '../../../components/hostel/superadmin/SuperAdminLayout';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const ManageApplication = ({ onNavigate, userInfo }) => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterHostel, setFilterHostel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBatch, setFilterBatch] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'
  const [adminRemarks, setAdminRemarks] = useState('');
  const [selectedHostelId, setSelectedHostelId] = useState('');
  const [hostels, setHostels] = useState([]);

  // Edit Student State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    full_name: '',
    phone_number: '',
    email: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    course: '',
    department: '',
    batch_name: '',
    student_id: '',
    guardian_name: '',
    guardian_phone: '',
    permanent_address: '',
    city: '',
    state: '',
    pincode: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    medical_conditions: '',
    allergies: '',
    special_requirements: ''
  });



  const handleEditClick = (application) => {
    setSelectedApplication(application);
    setEditFormData({
      full_name: application.full_name || '',
      phone_number: application.phone_number || '',
      email: application.email || '',
      date_of_birth: application.date_of_birth ? new Date(application.date_of_birth).toISOString().split('T')[0] : '',
      gender: application.gender || '',
      blood_group: application.blood_group || '',
      course: application.course || '',
      department: application.department || '',
      batch_name: application.batch_name || '',
      student_id: application.student_id || '',
      guardian_name: application.guardian_name || '',
      guardian_phone: application.guardian_phone || '',
      permanent_address: application.address || application.permanent_address || '',
      city: application.city || '',
      state: application.state || '',
      pincode: application.pincode || '',
      emergency_contact_name: application.emergency_contact_name || '',
      emergency_contact_phone: application.emergency_contact_phone || '',
      medical_conditions: application.medical_conditions || '',
      allergies: application.allergies || '',
      special_requirements: application.special_requirements || ''
    });
    setShowEditModal(true);
  };



  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!window.confirm('Are you sure you want to update student details?')) return;

    try {
      const response = await fetch(`${API_URL}/superadmin/update-application/${userInfo.userId}/${selectedApplication.id || selectedApplication.application_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editFormData })
      });
      const data = await response.json();
      if (data.success) {
        alert('Student details updated successfully');
        setShowEditModal(false);
        fetchApplications();
      } else {
        alert(data.message || 'Failed to update details');
      }
    } catch (err) {
      console.error(err);
      alert('Error updating details');
    }
  };



  useEffect(() => {
    if (userInfo?.userId) {
      fetchApplications();
      fetchHostels();
    }
  }, [userInfo]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/superadmin/applications/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setApplications(data.applications || []);
      } else {
        setError(data.message || 'Failed to load applications');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchHostels = async () => {
    try {
      const response = await fetch(`${API_URL}/superadmin/hostels/${userInfo.userId}`);
      const data = await response.json();

      if (data.success) {
        setHostels(data.hostels || []);
      }
    } catch (err) {
      console.error('Error fetching hostels:', err);
    }
  };

  const handleViewDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailsModal(true);
  };

  const handleOpenAction = (application, action) => {
    setSelectedApplication(application);
    setActionType(action);
    setAdminRemarks('');
    setSelectedHostelId(application.hostel_id || ''); // Set preferred hostel as default
    setShowActionModal(true);
  };

  const handleSubmitAction = async (e) => {
    e.preventDefault();

    // For approval, validate hostel selection
    if (actionType === 'approve' && !selectedHostelId) {
      alert('Please select a hostel to assign');
      return;
    }

    try {
      let response;

      if (actionType === 'approve') {
        // Use new super admin approve and assign API
        response = await fetch(
          `${API_URL}/superadmin/approve-and-assign/${selectedApplication.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              superAdminId: userInfo.userId,
              assignedHostelId: parseInt(selectedHostelId),
              remarks: adminRemarks || 'Approved by Super Admin'
            })
          }
        );
      } else {
        // Use new super admin reject API
        response = await fetch(
          `${API_URL}/superadmin/reject/${selectedApplication.id}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              superAdminId: userInfo.userId,
              remarks: adminRemarks
            })
          }
        );
      }

      const data = await response.json();

      if (data.success) {
        if (actionType === 'approve') {
          alert(`✅ Application approved successfully!\n\nAssigned to: ${data.assignedHostel}\n\nThe application is now pending approval from the hostel admin.\n\nApplicant will be notified`);
        } else {
          alert(`❌ Application rejected successfully!\n\nApplicant will be notified`);
        }

        setShowActionModal(false);
        setSelectedApplication(null);
        setActionType('');
        setAdminRemarks('');
        setSelectedHostelId('');
        fetchApplications();
      } else {
        alert(data.message || 'Failed to update application');
      }
    } catch (err) {
      console.error('Error updating application:', err);
      alert('Failed to update application. Please try again.');
    }
  };

  // Extract unique batch names for filter dropdown
  const uniqueBatches = [...new Set(applications.map(a => a.batch_name).filter(Boolean))].sort();

  const filteredApplications = applications.filter(application => {
    const matchesSearch =
      application.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      application.application_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      application.phone_number?.includes(searchQuery) ||
      application.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      application.course?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesHostel = filterHostel === 'all' || application.hostel_name === filterHostel;
    const approvalStatus = application.approval_status || 'pending_superadmin';
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'approved_forwarded' ? ['pending_hostel_admin', 'fully_approved'].includes(approvalStatus) : approvalStatus === filterStatus);
    const matchesBatch = filterBatch === 'all' || application.batch_name === filterBatch;

    return matchesSearch && matchesHostel && matchesStatus && matchesBatch;
  });

  const getStatusBadgeClass = (approvalStatus) => {
    switch (approvalStatus) {
      case 'pending_superadmin': return 'status-warning';
      case 'pending_hostel_admin': return 'status-active'; // Changed to active color
      case 'fully_approved': return 'status-active';
      case 'rejected': return 'status-danger';
      default: return 'status-default';
    }
  };

  const getStatusDisplay = (application) => {
    const approvalStatus = application.approval_status || 'pending_superadmin';

    switch (approvalStatus) {
      case 'pending_superadmin':
        return 'PENDING';
      case 'pending_hostel_admin':
        return 'APPROVED & FORWARDED'; // Combined label
      case 'fully_approved':
        return 'APPROVED & FORWARDED'; // Combined label
      case 'rejected':
        return 'REJECTED';
      default:
        return 'PENDING';
    }
  };

  return (
    <SuperAdminLayout
      onNavigate={onNavigate}
      userInfo={userInfo}
      activePage="hostel-superadmin-manage-application"
      pageTitle="All Applications 📋"
    >
      <style>{`
        .controls-section {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-box {
          flex: 1;
          min-width: 300px;
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 50px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .search-icon {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 20px;
          color: #52ab98;
        }

        .filter-select {
          padding: 14px 20px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          color: #2b6777;
          background: white;
          cursor: pointer;
          transition: all 0.3s;
        }

        .filter-select:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .stats-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          padding: 24px;
          border-radius: 16px;
          border: 2px solid rgba(82,171,152,0.15);
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
        }

        .applications-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
          gap: 24px;
        }

        .application-card {
          background: white;
          border-radius: 20px;
          padding: 28px;
          border: 2px solid rgba(82,171,152,0.15);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .application-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 6px;
          height: 100%;
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .application-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.3);
        }

        .application-header {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 20px;
        }

        .application-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 28px;
          color: white;
          font-weight: 800;
          flex-shrink: 0;
        }

        .application-info {
          flex: 1;
        }

        .application-number {
          font-size: 14px;
          font-weight: 800;
          color: #52ab98;
          margin-bottom: 4px;
        }

        .application-name {
          font-size: 18px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .application-date {
          font-size: 12px;
          color: rgba(43,103,119,0.6);
        }

        .application-details {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .detail-label {
          font-size: 11px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .detail-value {
          font-size: 13px;
          font-weight: 700;
          color: #2b6777;
          word-break: break-word;
        }

        .application-actions {
          display: flex;
          gap: 10px;
          padding-top: 20px;
          border-top: 2px solid rgba(82,171,152,0.1);
        }

        .action-btn-view,
        .action-btn-approve,
        .action-btn-reject,
        .action-btn-edit {
          flex: 1;
          padding: 12px;
          border: none;
          border-radius: 12px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }

        .action-btn-view {
          background: rgba(82,171,152,0.15);
          color: #2b6777;
        }

        .action-btn-view:hover {
          background: rgba(82,171,152,0.25);
          transform: translateY(-2px);
        }

        .action-btn-approve {
          background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(34,197,94,0.3);
        }

        .action-btn-approve:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(34,197,94,0.4);
        }

        .action-btn-approve:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .action-btn-reject {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: white;
          box-shadow: 0 4px 15px rgba(239,68,68,0.3);
        }

        .action-btn-reject:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 25px rgba(239,68,68,0.4);
        }
        
        .action-btn-reject:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .action-btn-edit {
          background: rgba(245, 158, 11, 0.15);
          color: #d97706;
        }

        .action-btn-edit:hover {
          background: rgba(245, 158, 11, 0.25);
          transform: translateY(-2px);
        }



        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }

        .status-active {
          background: rgba(34,197,94,0.15);
          color: #16a34a;
        }

        .status-warning {
          background: rgba(251,146,60,0.15);
          color: #ea580c;
        }

        .status-info {
          background: rgba(59,130,246,0.15);
          color: #2563eb;
        }

        .status-danger {
          background: rgba(239,68,68,0.15);
          color: #dc2626;
        }
        
        .status-default {
            background: rgba(107,114,128,0.15);
            color: #4b5563;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }

        .modal {
          background: white;
          border-radius: 24px;
          width: 100%;
          max-width: 900px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
        }

        .modal-header {
          padding: 28px 32px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.15);
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 12px;
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.25);
          transform: rotate(90deg);
        }

        .modal-body {
          padding: 32px;
        }

        .detail-row {
          display: grid;
          grid-template-columns: 200px 1fr;
          gap: 16px;
          padding: 16px 0;
          border-bottom: 1px solid rgba(82,171,152,0.1);
        }
        
        .detail-row:last-child {
            border-bottom: none;
        }

        .detail-row-label {
          font-size: 13px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
          text-transform: uppercase;
        }

        .detail-row-value {
          font-size: 15px;
          font-weight: 600;
          color: #2b6777;
          word-break: break-word;
        }

        .form-group {
          margin-bottom: 24px;
        }

        .form-label {
          display: block;
          font-size: 14px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .form-textarea {
          width: 100%;
          padding: 14px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 15px;
          font-family: 'Inter', sans-serif;
          transition: all 0.3s;
          min-height: 120px;
          resize: vertical;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 4px rgba(82,171,152,0.1);
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-cancel,
        .btn-submit-approve,
        .btn-submit-reject {
          padding: 14px 28px;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-cancel {
          background: rgba(107,114,128,0.15);
          color: #374151;
        }

        .btn-cancel:hover {
          background: rgba(107,114,128,0.25);
        }

        .btn-submit-approve {
          background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(34,197,94,0.3);
        }

        .btn-submit-approve:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(34,197,94,0.4);
        }
        
        .btn-submit-reject {
          background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
          color: white;
          box-shadow: 0 4px 20px rgba(239,68,68,0.3);
        }

        .btn-submit-reject:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(239,68,68,0.4);
        }

        .empty-state {
          text-align: center;
          padding: 80px 20px;
          color: #666;
        }

        .empty-state-icon {
          font-size: 80px;
          margin-bottom: 20px;
        }

        .empty-state-text {
          font-size: 18px;
          color: #2b6777;
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .applications-grid {
            grid-template-columns: 1fr;
          }

          .controls-section {
            flex-direction: column;
          }

          .search-box {
            width: 100%;
          }

          .stats-row {
            grid-template-columns: repeat(2, 1fr);
          }

          .application-actions {
            flex-direction: column;
          }
        }
      `}</style>

      {/* Stats Row */}
      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">Total</div>
          <div className="stat-value">{applications.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending</div>
          <div className="stat-value">
            {applications.filter(a => (a.approval_status || 'pending_superadmin') === 'pending_superadmin').length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Approved & Forwarded</div>
          <div className="stat-value">
            {applications.filter(a => ['pending_hostel_admin', 'fully_approved'].includes(a.approval_status)).length}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Rejected</div>
          <div className="stat-value">
            {applications.filter(a => (a.approval_status || 'pending_superadmin') === 'rejected').length}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="controls-section">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, application number, phone, email, or course..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterHostel}
          onChange={(e) => setFilterHostel(e.target.value)}
        >
          <option value="all">All Hostels</option>
          {hostels.map(hostel => (
            <option key={hostel.hostel_id} value={hostel.hostel_name}>
              {hostel.hostel_name}
            </option>
          ))}
        </select>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="pending_superadmin">⏳ Pending Super Admin</option>
          <option value="approved_forwarded">✅ Approved & Forwarded</option>
          <option value="rejected">❌ Rejected</option>
        </select>

        <select
          className="filter-select"
          value={filterBatch}
          onChange={(e) => setFilterBatch(e.target.value)}
        >
          <option value="all">All Batches</option>
          {uniqueBatches.map(batch => (
            <option key={batch} value={batch}>
              {batch}
            </option>
          ))}
        </select>
      </div>

      {/* Applications Grid */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">⏳</div>
          <div className="empty-state-text">Loading applications...</div>
        </div>
      ) : error ? (
        <div className="empty-state">
          <div className="empty-state-icon">❌</div>
          <div className="empty-state-text">{error}</div>
        </div>
      ) : filteredApplications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <div className="empty-state-text">No applications found</div>
        </div>
      ) : (
        <div className="applications-grid">
          {filteredApplications.map((application) => (
            <div key={application.id} className="application-card">
              <div className="application-header">
                <div className="application-icon">
                  {application.full_name?.charAt(0) || '?'}
                </div>
                <div className="application-info">
                  <div className="application-number">{application.application_number}</div>
                  <div className="application-name">{application.full_name}</div>
                  <div className="application-date">
                    {new Date(application.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              <div className="application-details">
                <div className="detail-item">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{application.phone_number}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{application.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Course</span>
                  <span className="detail-value">{application.course}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Department</span>
                  <span className="detail-value">{application.department}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Hostel</span>
                  <span className="detail-value">{application.hostel_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${getStatusBadgeClass(application.approval_status || 'pending_superadmin')}`}>
                    {getStatusDisplay(application)}
                  </span>
                </div>
              </div>

              <div className="application-actions">
                <button
                  className="action-btn-view"
                  onClick={() => handleViewDetails(application)}
                >
                  👁️ View
                </button>
                <button
                  className="action-btn-edit"
                  onClick={() => handleEditClick(application)}
                >
                  ✏️ Edit
                </button>

                {(application.approval_status === 'pending_superadmin' || !application.approval_status) && (
                  <>
                    <button
                      className="action-btn-approve"
                      onClick={() => handleOpenAction(application, 'approve')}
                    >
                      ✅ Approve
                    </button>
                    <button
                      className="action-btn-reject"
                      onClick={() => handleOpenAction(application, 'reject')}
                    >
                      ❌ Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Application Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="detail-row">
                <div className="detail-row-label">Application Number</div>
                <div className="detail-row-value">{selectedApplication.application_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Full Name</div>
                <div className="detail-row-value">{selectedApplication.full_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Date of Birth</div>
                <div className="detail-row-value">{selectedApplication.date_of_birth ? new Date(selectedApplication.date_of_birth).toLocaleDateString() : '-'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Gender</div>
                <div className="detail-row-value" style={{ textTransform: 'capitalize' }}>{selectedApplication.gender || '-'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Blood Group</div>
                <div className="detail-row-value">{selectedApplication.blood_group || '-'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Phone Number</div>
                <div className="detail-row-value">{selectedApplication.phone_number}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Email</div>
                <div className="detail-row-value">{selectedApplication.email}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Course</div>
                <div className="detail-row-value">{selectedApplication.course}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Batch</div>
                <div className="detail-row-value">{selectedApplication.batch_name || '-'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Student ID</div>
                <div className="detail-row-value">{selectedApplication.student_id || '-'}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Department</div>
                <div className="detail-row-value">{selectedApplication.department}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Hostel</div>
                <div className="detail-row-value">{selectedApplication.hostel_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Room Type</div>
                <div className="detail-row-value">{selectedApplication.room_type}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Guardian Name</div>
                <div className="detail-row-value">{selectedApplication.guardian_name}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Guardian Phone</div>
                <div className="detail-row-value">{selectedApplication.guardian_phone}</div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Emergency Contact</div>
                <div className="detail-row-value">
                  {selectedApplication.emergency_contact_name} ({selectedApplication.emergency_contact_phone})
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Address</div>
                <div className="detail-row-value">
                  {selectedApplication.address}<br />
                  {selectedApplication.city}, {selectedApplication.state} - {selectedApplication.pincode}
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Status</div>
                <div className="detail-row-value">
                  <span className={`status-badge ${getStatusBadgeClass(selectedApplication.approval_status || 'pending_superadmin')}`}>
                    {getStatusDisplay(selectedApplication)}
                  </span>
                </div>
              </div>
              <div className="detail-row">
                <div className="detail-row-label">Application Date</div>
                <div className="detail-row-value">
                  {new Date(selectedApplication.created_at).toLocaleString()}
                </div>
              </div>
              {selectedApplication.admin_remarks && (
                <div className="detail-row">
                  <div className="detail-row-label">Admin Remarks</div>
                  <div className="detail-row-value">{selectedApplication.admin_remarks}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Approve/Reject) */}
      {showActionModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowActionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {actionType === 'approve' ? '✅ Approve Application' : '❌ Reject Application'}
              </h2>
              <button className="modal-close" onClick={() => setShowActionModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmitAction}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Application Number</label>
                  <input
                    type="text"
                    className="form-textarea"
                    value={selectedApplication.application_number}
                    disabled
                    style={{
                      background: '#f3f4f6',
                      cursor: 'not-allowed',
                      minHeight: 'auto',
                      padding: '14px 18px'
                    }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Applicant Name</label>
                  <input
                    type="text"
                    className="form-textarea"
                    value={selectedApplication.full_name}
                    disabled
                    style={{
                      background: '#f3f4f6',
                      cursor: 'not-allowed',
                      minHeight: 'auto',
                      padding: '14px 18px'
                    }}
                  />
                </div>

                {actionType === 'approve' && (
                  <>
                    <div className="form-group" style={{
                      background: 'rgba(251,146,60,0.1)',
                      border: '2px solid rgba(251,146,60,0.3)',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '20px'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '800',
                        color: '#ea580c',
                        marginBottom: '8px'
                      }}>
                        Student's Preferred Hostel:
                      </div>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: '900',
                        color: '#2b6777'
                      }}>
                        {selectedApplication.hostel_name || 'Not specified'}
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Assign Hostel *</label>
                      <select
                        className="form-textarea"
                        value={selectedHostelId}
                        onChange={(e) => setSelectedHostelId(e.target.value)}
                        required
                        style={{
                          minHeight: 'auto',
                          padding: '14px 18px'
                        }}
                      >
                        <option value="">-- Select a Hostel to Assign --</option>
                        {hostels.map(hostel => (
                          <option
                            key={hostel.hostel_id}
                            value={hostel.hostel_id}
                            disabled={hostel.available_rooms <= 0}
                          >
                            {hostel.hostel_name} - Available: {hostel.available_rooms}/{hostel.total_rooms} beds
                            {hostel.available_rooms <= 0 ? ' (FULL)' : ''}
                          </option>
                        ))}
                      </select>
                      <div style={{
                        fontSize: '12px',
                        color: 'rgba(43,103,119,0.6)',
                        marginTop: '8px',
                        fontStyle: 'italic'
                      }}>
                        You can assign any hostel regardless of student's preference
                      </div>
                    </div>
                  </>
                )}

                <div className="form-group">
                  <label className="form-label">
                    {actionType === 'approve' ? 'Approval' : 'Rejection'} Remarks
                    {actionType === 'reject' && ' *'}
                  </label>
                  <textarea
                    className="form-textarea"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                    placeholder={`Enter your ${actionType === 'approve' ? 'approval' : 'rejection'} remarks...`}
                    required={actionType === 'reject'}
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowActionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={actionType === 'approve' ? 'btn-submit-approve' : 'btn-submit-reject'}
                >
                  {actionType === 'approve'
                    ? '✅ Approve & Assign Hostel'
                    : '❌ Reject Application'
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {showEditModal && selectedApplication && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">✏️ Edit Application Details</h2>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input type="text" name="full_name" value={editFormData.full_name} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <input type="text" name="phone_number" value={editFormData.phone_number} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                </div>

                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input type="email" name="email" value={editFormData.email} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date of Birth</label>
                    <input type="date" name="date_of_birth" value={editFormData.date_of_birth} onChange={handleEditInputChange} className="search-input" />
                  </div>
                </div>

                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Gender</label>
                    <select name="gender" value={editFormData.gender} onChange={handleEditInputChange} className="search-input">
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Blood Group</label>
                    <input type="text" name="blood_group" value={editFormData.blood_group} onChange={handleEditInputChange} className="search-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Student ID</label>
                    <input type="text" name="student_id" value={editFormData.student_id} onChange={handleEditInputChange} className="search-input" />
                  </div>
                </div>

                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Course</label>
                    <input type="text" name="course" value={editFormData.course} onChange={handleEditInputChange} className="search-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Department</label>
                    <input type="text" name="department" value={editFormData.department} onChange={handleEditInputChange} className="search-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch Name</label>
                    <input type="text" name="batch_name" value={editFormData.batch_name} onChange={handleEditInputChange} className="search-input" />
                  </div>
                </div>

                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Guardian Name</label>
                    <input type="text" name="guardian_name" value={editFormData.guardian_name} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Guardian Phone</label>
                    <input type="text" name="guardian_phone" value={editFormData.guardian_phone} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Permanent Address</label>
                  <textarea name="permanent_address" value={editFormData.permanent_address} onChange={handleEditInputChange} className="form-textarea" style={{ minHeight: '80px' }} required />
                </div>

                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">City</label>
                    <input type="text" name="city" value={editFormData.city} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <input type="text" name="state" value={editFormData.state} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pincode</label>
                    <input type="text" name="pincode" value={editFormData.pincode} onChange={handleEditInputChange} className="search-input" required />
                  </div>
                </div>

                <div className="detail-row" style={{ gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div className="form-group">
                    <label className="form-label">Emergency Contact Name</label>
                    <input type="text" name="emergency_contact_name" value={editFormData.emergency_contact_name} onChange={handleEditInputChange} className="search-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Emergency Contact Phone</label>
                    <input type="text" name="emergency_contact_phone" value={editFormData.emergency_contact_phone} onChange={handleEditInputChange} className="search-input" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Medical Conditions</label>
                  <textarea name="medical_conditions" value={editFormData.medical_conditions} onChange={handleEditInputChange} className="form-textarea" style={{ minHeight: '60px' }} />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" className="btn-submit-approve">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}



    </SuperAdminLayout>
  );
};

export default ManageApplication;